import React, { useContext, useMemo, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import { CollectionClientDetail, CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';
import { formatDollar, formatOptionalDate } from '../utils/format';
import { useInvoicePdfUpload } from '../hooks/useInvoicePdfUpload';
import { useInvoicePdfDelete } from '../hooks/useInvoicePdfDelete';
import { groupInvoicesByDepartment } from '../utils/groupInvoices';
import DepartmentGroupedInvoiceSections from '../../shared/invoices/DepartmentGroupedInvoiceSections';
import InvoicePdfCell from '../../shared/invoices/InvoicePdfCell';
import AdjustDueDateModal from './AdjustDueDateModal';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { usePut } from '../../../sharedModules/hooks/useQuery';
import { useCollectionsPermissions } from '../../shared/useCollectionsPermissions';

interface Props {
  documents: CollectionInvoiceDocument[];
  location: string;
  acctNo: string;
  company: string;
  clientId: number | null;
  onDueDateAdjusted?: (client: CollectionClientDetail) => void;
  onUploaded: (applyTo: string, pdf: CollectionInvoicePdf) => void;
  onDeleted: (applyTo: string, pdfId: number) => void;
  noCard?: boolean;
}

const buildColumns = (
  triggerUpload: (applyTo: string) => void,
  uploadingApplyTo: string | null,
  canUpload: boolean,
  deletePdf: (applyTo: string, pdfId: number, filename: string) => void,
  deletingPdfId: number | null,
  onEditDueDate: ((row: CollectionInvoiceDocument) => void) | null,
  editDisabledReason: string | null,
) => [
  {
    name: 'Invoice #',
    selector: (row: CollectionInvoiceDocument) => row.apply_to,
    width: '130px',
  },
  {
    name: 'Invoice Date',
    selector: (row: CollectionInvoiceDocument) => formatOptionalDate(row.invoice_date),
    width: '130px',
  },
  {
    name: 'Due Date',
    selector: (row: CollectionInvoiceDocument) => formatOptionalDate(row.due_date),
    width: '160px',
    cell: (row: CollectionInvoiceDocument) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2">{formatOptionalDate(row.due_date)}</Typography>
        {onEditDueDate && (
          <Tooltip title={editDisabledReason ?? 'Edit due date'}>
            {/* span keeps the tooltip working on a disabled button */}
            <span>
              <IconButton
                size="small"
                aria-label={`Edit due date for invoice ${row.apply_to}`}
                disabled={!!editDisabledReason}
                onClick={() => onEditDueDate(row)}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    ),
  },
  {
    name: 'Amount',
    selector: (row: CollectionInvoiceDocument) => row.amount,
    cell: (row: CollectionInvoiceDocument) => <Typography variant="body2">{formatDollar(row.amount)}</Typography>,
    right: true,
    width: '170px',
  },
  {
    name: 'PDF',
    selector: (row: CollectionInvoiceDocument) => row.pdf?.filename ?? '',
    grow: 2,
    cell: (row: CollectionInvoiceDocument) =>
      row.pdf ? (
        <InvoicePdfCell pdf={row.pdf} applyTo={row.apply_to} deletingPdfId={deletingPdfId} onDelete={deletePdf} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={!canUpload || uploadingApplyTo === row.apply_to}
            onClick={() => triggerUpload(row.apply_to)}
          >
            {uploadingApplyTo === row.apply_to ? 'Uploading…' : 'Upload Invoice'}
          </Button>
        </Box>
      ),
  },
];

const ClientInvoicesTable: React.FC<Props> = ({
  documents,
  location,
  acctNo,
  company,
  clientId,
  onDueDateAdjusted,
  onUploaded,
  onDeleted,
  noCard = false,
}) => {
  const { fileInputRef, triggerUpload, handleFileSelected, uploadingApplyTo, canUpload } = useInvoicePdfUpload({
    location,
    acctNo,
    onUploaded: ({ applyTo, pdf }) => onUploaded(applyTo, pdf),
  });
  const { deletePdf, deletingPdfId } = useInvoicePdfDelete({ onDeleted });
  const { showSuccessSnackBar } = useContext(SnackBarContext);
  const { isCollectionsAdmin } = useCollectionsPermissions();
  const adjustMutation = usePut<CollectionClientDetail>();
  const [editing, setEditing] = useState<CollectionInvoiceDocument | null>(null);
  const [adjustError, setAdjustError] = useState<string | null>(null);

  const onEditDueDate = isCollectionsAdmin
    ? (row: CollectionInvoiceDocument) => {
        setAdjustError(null);
        setEditing(row);
      }
    : null;
  const editDisabledReason = clientId ? null : 'No collections client record';

  const handleConfirmDueDate = async (dueDate: string, reason: string) => {
    if (!editing || !clientId) return;
    try {
      const res = await adjustMutation.mutateAsync({
        url: `/collections/clients/${clientId}/invoices/${encodeURIComponent(editing.apply_to)}/due_date`,
        data: { due_date_adjustment: { due_date: dueDate, reason } },
      });
      setEditing(null);
      showSuccessSnackBar?.(`Invoice #${editing.apply_to} due date updated.`);
      onDueDateAdjusted?.(res.data);
    } catch (error: any) {
      const data = error?.response?.data;
      setAdjustError(data?.errors?.join(' ') || data?.error || 'Failed to update due date.');
    }
  };

  const columns = buildColumns(
    triggerUpload,
    uploadingApplyTo,
    canUpload,
    deletePdf,
    deletingPdfId,
    onEditDueDate,
    editDisabledReason,
  );

  const groups = useMemo(() => groupInvoicesByDepartment(documents), [documents]);
  const totalAmount = useMemo(() => documents.reduce((sum, invoice) => sum + invoice.amount, 0), [documents]);
  const namedDepartmentCount = useMemo(() => groups.filter((group) => !group.isUngrouped).length, [groups]);
  const hasUngroupedGroup = useMemo(() => groups.some((group) => group.isUngrouped), [groups]);

  const summaryText = (
    <>
      <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
        {documents.length}
      </Box>{' '}
      collection {documents.length === 1 ? 'invoice' : 'invoices'} in{' '}
      <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
        {namedDepartmentCount}
      </Box>{' '}
      {namedDepartmentCount === 1 ? 'department' : 'departments'}
      {hasUngroupedGroup ? ' + ungrouped' : ''} with past due of{' '}
      <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
        {formatDollar(totalAmount)}
      </Box>
    </>
  );

  const content =
    documents.length === 0 ? (
      <Box sx={{ p: 3, color: 'text.secondary' }}>No past-due invoices.</Box>
    ) : (
      <DepartmentGroupedInvoiceSections
        groups={groups}
        columns={columns}
        summaryText={summaryText}
        emptyMessage="No past-due invoices."
      />
    );

  return (
    <>
      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFileSelected} />
      {editing && (
        <AdjustDueDateModal
          applyTo={editing.apply_to}
          company={company}
          currentDueDate={editing.due_date}
          isSubmitting={adjustMutation.isPending}
          error={adjustError}
          onClose={() => setEditing(null)}
          onConfirm={handleConfirmDueDate}
        />
      )}
      {noCard ? <Box sx={{ p: 2 }}>{content}</Box> : content}
    </>
  );
};

export default ClientInvoicesTable;
